import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import * as queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import TennisAustraliaForm from './TennisAustraliaForm';
import Img from '../../../components/Img';
import { postTennisAustraliaV2, skipTennisAustraliaV2 } from '../../../actions/onboarding-actions';
import config from '../../../../config';
import { getApiAxiosInstance } from '../../../utils/network';
import { getLinkedAccounts, getAustraliaSettings, updateAustraliaSettings } from '../../../api/settingsApi';

import styles from '../nuoV2.scss';
import { isUtrActivationInProgress } from '../../../utils/activateUtr';
import { useStatsig } from '../../../components/hooks/useStatsig';
import { showErrorPopup } from '../../../actions/notification-actions';
import { linkedAccountTypes } from '../../../utils/oauth';

const TennisAustralia = (props) => {
    const { activateUtr } = props;
    const dispatch = useDispatch();
    const location = useLocation();
    const parsed = queryString.parse(location.search);
    const ACCT_ALREADY_LINKED_ERROR = 'acct_already_linked';
    const isActivationInProgress = isUtrActivationInProgress(activateUtr);
    const { checkGate, featureGates } = useStatsig();
    const tennisAustraliaOAuthEnabled = checkGate(featureGates.TENNIS_AUSTRALIA_OAUTH);

    useEffect(() => {
        if (tennisAustraliaOAuthEnabled) {
            const checkAndUpdate = async () => {
                try {
                    const axiosInstance = getApiAxiosInstance();
                    const accountLinkResponse = await getLinkedAccounts(axiosInstance);
                    // Look for a linked Tennis Australia account
                    const accounts = accountLinkResponse?.data?.memberThirdPartyAccounts || [];
                    const isLinked = accounts.some(acc => acc.type === linkedAccountTypes.TENNIS_AUSTRALIA);
                    if (isLinked) {
                        const response = await getAustraliaSettings(axiosInstance);
                        const marketingEmailsOptIn = response?.data?.marketingEmailsOptIn;
                        // increment the onboarding step which requires the marketing email opt in selection they made before getting redirected back
                        // PSA TA Link accounts does not increment NUO so this is required to increment the step once we determine if accounts are linked(if they got redirected back)
                        await dispatch(postTennisAustraliaV2({
                            marketingEmailsOptIn,
                        }));
                    }
                } catch (err) {
                    dispatch(showErrorPopup({ showSupportLink: true }));
                }
            };
            checkAndUpdate();
        }
    }, []);

    useEffect(() => {
        if (parsed?.error) {
             if (parsed?.error === ACCT_ALREADY_LINKED_ERROR) {
                dispatch(showErrorPopup({ message: 'This account is already linked to another UTR Sports account', showSupportLink: true }));
            } else {
                dispatch(showErrorPopup({ showSupportLink: true }));
            }
        }
    }, [parsed?.error]);

    const handleMarketingToggle = async (e) => {
        const { checked } = e.target;
        try {
            const axiosInstance = getApiAxiosInstance();
            await updateAustraliaSettings(axiosInstance, { marketingEmailsOptIn: checked });
        } catch (err) {
            dispatch(showErrorPopup({ showSupportLink: true }));
        }
    };

    const handleLinkAccount = () => {
        const returnTo = `${config.appHost}/onboarding`;
        window.location.href = `${config.apiHost}/v1/oauth/ta/initiate-link?platform=web&source=internal&flow=link&returnTo=${returnTo}`;
    };

    const handleSubmit = (values) => {
        const { tennisConnectId } = values;
        const payload = {
            tennisConnectId,
            marketingEmailsOptIn: 'true',
        };

        dispatch(postTennisAustraliaV2(payload));
    };

    const handleSkip = () => {
        dispatch(skipTennisAustraliaV2());
    };

    if (isActivationInProgress) {
        return null;
    }

    return (
        <>
            {tennisAustraliaOAuthEnabled ? (
                <div style={{ padding: 32 }} className="d-flex flex-column align-items-center justify-content-center bg-white">
                    <div className="mb-4">
                        <Img src="/img/social/tennis-australia-logo.svg" alt="Tennis Australia" data-testid="nuo-v2.tennis-australia.ta-logo" />
                    </div>
                    <div className="text-black mb-4 fs-14" data-testid="nuo-v2.tennis-australia.description">
                    UTR Sports and Tennis Australia have partnered together to make the UTR Rating the official rating of tennis in Australia. To play competitive and recreation tennis in Australia, you'll need to link your UTR Sports and Tennis Australia accounts.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, justifyContent: 'center' }}>
                        <input
                            type="checkbox"
                            id="marketingEmailsOptIn"
                            style={{ marginRight: 12, width: 20, height: 20 }}
                            onChange={handleMarketingToggle}
                            data-testid="nuo-v2.tennis-australia.marketing-email-checkbox"
                        />
                        <label htmlFor="marketingEmailsOptIn" className="text-black fs-14" data-testid="nuo-v2.tennis-australia.marketing-email-label">
                        I would like to receive email communication from UTR Sports Including product updates, marketing initiatives, content and more
                        </label>
                    </div>
                    <button
                        className="btn btn-primary btn-xl text-uppercase btn-block mb-4"
                        onClick={handleLinkAccount}
                        type="button"
                        data-testid="nuo-v2.tennis-australia.link-account-btn"
                    >
                    LINK MY TENNIS AUSTRALIA ACCOUNT
                    </button>
                    <div>
                        <a href="#" onClick={e => { e.preventDefault(); handleSkip(); }} data-testid="nuo-v2.tennis-australia.skip-link">
                        Link account later in Settings
                        </a>
                    </div>
                </div>
        ) : (
            <div className={styles.pageContent}>
                <div className={styles.title} data-testid="nuo-v2.tennis-australia.title">
                    Tennis ID
                </div>
                <div
                    className={`link clickable d-inline-block mr32 mb16`}
                    onClick={handleSkip}
                    data-testid="nuo-v2.tennis-australia.skip-link"
                >
                    I&apos;ll do this later.
                </div>
                <div className={styles.description}>
                    UTR Sports and Tennis Australia have partnered together to make the UTR Rating the official rating of tennis in Australia. To play competitive and recreational tennis in Australia, You&apos;ll need to link your UTR Sports and Tennis Australia accounts through your Tennis ID.
                </div>
                <div className={styles.description}>
                    Please note, your Tennis ID is required in order for you to use your UTR Rating to play in Australia and to see the rating in your Tennis profile. Once you create your Tennis.com.au account. come back and add the Tennis ID in your account settings.
                </div>
                <div className={styles.description}>
                    Need a Tennis ID? You can sign up for it or log in and find it under your name in your profile at <a href="https://tennis.com.au" target="_blank" rel="noopener noreferrer" data-testid="nuo-v2.tennis-australia.tennis-com-link">https://tennis.com.au</a>
                </div>
                <div className={styles.description}>
                    <Img src="/img/tennis-connect-id-finder.png" className="w-100" data-testid="nuo-v2.tennis-australia.id-finder-img" />
                </div>
                <TennisAustraliaForm {...props} handleSubmit={handleSubmit} />
            </div>
        )}
        </>
    );
};

export default TennisAustralia;
